import React, { useState } from 'react';
import { ListViewData, ListView } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface SaveViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => void;
    currentConfig: ListViewData;
    isAdmin: boolean;
    isSaving?: boolean;
    existingDefaultView?: ListView | null;
}

export const SaveViewModal: React.FC<SaveViewModalProps> = ({
    isOpen,
    onClose,
    onSave,
    currentConfig,
    isAdmin,
    isSaving = false,
    existingDefaultView = null,
}) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<'private' | 'public' | 'public-default'>('private');
    const [description, setDescription] = useState('');

    const handleSave = () => {
        if (name.trim()) {
            const isDefault = type === 'public-default';
            const viewType = type === 'public-default' ? 'public' : type;
            onSave(name.trim(), viewType, description.trim() || undefined, isDefault);
            // Reset form
            setName('');
            setType('private');
            setDescription('');
        }
    };

    const handleCancel = () => {
        setName('');
        setType('private');
        setDescription('');
        onClose();
    };

    if (!isOpen) return null;

    const visibleColumnsCount = Object.values(currentConfig.columnVisibility).filter(Boolean).length;

    return (
        <ModalWrapper modalId="SaveViewModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Save List View"
            isLoading={isSaving}
        >
            {/* View Summary */}
            <div className="text-sm text-[#366A75]/80 mb-6 p-4 bg-[#EBF5F5] rounded-2xl">
                <div className="font-semibold mb-1">Current Configuration:</div>
                <div className="space-y-0.5">
                    <div>• {visibleColumnsCount} columns visible</div>
                    <div>• Sorted by: {currentConfig.sortColumn} ({currentConfig.sortOrder})</div>
                </div>
            </div>

            {/* View Name Input */}
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="View Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    className={POPUP_STYLES.input}
                />
            </div>

            {/* Description Input */}
            <div className="mb-4">
                <div className="relative">
                    <textarea
                        placeholder="Enter notes"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent resize-none text-[#366A75]"
                    />
                    <span className="absolute right-4 top-3.5 text-[#366A75]/60">✎</span>
                </div>
            </div>

            {/* View Type Selection */}
            <div className="mb-6">
                <select
                    value={type}
                    onChange={(e) => setType(e.target.value as 'private' | 'public' | 'public-default')}
                    disabled={!isAdmin}
                    className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <option value="private">Private (Only visible to you)</option>
                    {isAdmin && <option value="public">Public (Visible to all users)</option>}
                    {isAdmin && !existingDefaultView && <option value="public-default">Public Default (Used as default for all users)</option>}
                </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
                <button
                    onClick={handleSave}
                    disabled={!name.trim() || isSaving}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
